/**
 * localStorage helpers for the notification system.
 *
 * Each session's state is stored under the key `l9-notif:v1:<sessionId>`.
 * This module provides typed accessors so the hook and provider can share
 * the same schema without leaking private implementation details.
 */

/* ----------------------------------------------------------------- */
/*  Schema                                                            */
/* ----------------------------------------------------------------- */

export interface StoredSessionState {
  /**
   * Whether this session was created or interacted with on this client
   * (via setRunStatus from the SSE path — i.e. the user sent a message
   * here). Only locallyTracked sessions get hasUnread checkmark
   * indicators and in-tab browser notifications from the foreground
   * polling system. Sessions not created on this client rely on the PWA
   * push system for cross-device notifications, since the polling system
   * cannot reliably track read state across devices.
   * Optional for backward-compatibility; defaults to false.
   */
  locallyTracked?: boolean;
  /** Timestamp ms when user last read this session */
  lastReadTimestamp: number;
  /** Most recent run status: 'idle' | 'queued' | 'running' | 'completed' | 'failed' | 'cancelled' */
  lastKnownRunStatus: string;
  /** run_id of the last known run we tracked */
  lastKnownRunId: string | null;
  /**
   * run_id of the last run we already fired a notification for. Prevents
   * re-notifying the same terminal run on subsequent poll ticks (e.g. after
   * a visibility toggle). When this equals lastKnownRunId for a terminal run,
   * the poller skips the notification. Optional for backward-compatibility.
   */
  lastNotifiedRunId?: string | null;
  /**
   * Message UUID of the last message we saw (cursor for ?after= pagination).
   * No longer advanced by the notification poller (which is now run-status-
   * only), but retained for backward-compatibility with existing localStorage
   * entries and for the `isKnown` heuristic.
   */
  lastKnownMessageId: string | null;
  /**
   * The session.last_active_at ISO string we last successfully checked.
   * When this equals the current last_active_at from the API we can skip
   * re-polling — nothing new has arrived since our last fetch.
   * Optional for backward-compatibility with old localStorage entries.
   */
  lastCheckedActiveAt?: string | null;
  /**
   * The session.last_active_at ISO string we last saw from the API. Used to
   * determine whether a thread is too old to be flagged as pending/unread.
   * Optional for backward-compatibility with old localStorage entries.
   */
  lastActiveAt?: string | null;
  /** Whether the session has messages newer than lastReadTimestamp.
   * Optional for backward-compatibility with old localStorage entries. */
  hasUnread?: boolean;
  /**
   * Timestamp ms of the last time we did expensive per-session polling for
   * this session. Used to throttle stale (≥12h inactive) sessions to ~5 min.
   * Optional for backward-compatibility with old localStorage entries.
   */
  lastCheckedAt?: number | null;
}

const STORAGE_PREFIX = "l9-notif:v1:";

/* ----------------------------------------------------------------- */
/*  Public API                                                        */
/* ----------------------------------------------------------------- */

/**
 * Load all stored session notification states from localStorage.
 */
export function loadAllStoredStates(): Map<string, StoredSessionState> {
  const map = new Map<string, StoredSessionState>();
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(STORAGE_PREFIX)) continue;
      try {
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw) as StoredSessionState;
          const id = key.slice(STORAGE_PREFIX.length);
          map.set(id, parsed);
        }
      } catch {
        // corrupt entry; skip
      }
    }
  } catch {
    // storage access denied (private browsing)
  }
  return map;
}

/**
 * Load the stored state for a single session.
 * Returns undefined if no stored state exists.
 */
export function loadSessionState(sessionId: string): StoredSessionState | undefined {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${sessionId}`);
    if (!raw) return undefined;
    return JSON.parse(raw) as StoredSessionState;
  } catch {
    return undefined;
  }
}

/**
 * Persist session notification state to localStorage.
 */
export function saveSessionState(sessionId: string, state: StoredSessionState): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${sessionId}`, JSON.stringify(state));
  } catch {
    // storage access denied
  }
}

/**
 * Remove stored notification state for a session (cleanup on archive/delete).
 */
export function removeSessionState(sessionId: string): void {
  try {
    localStorage.removeItem(`${STORAGE_PREFIX}${sessionId}`);
  } catch {
    // storage access denied
  }
}